import React, { useState, useRef, useMemo, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import {
  FiHome,
  FiUpload,
  FiX,
  FiCheck,
  FiChevronDown,
  FiMaximize2,
} from "react-icons/fi";
import { PiSparkleFill } from "react-icons/pi";
import "../../../assets/css/customer/decoraai/DecoraAi.css";
import axios from "axios";
import AppContext from "../../../context/AppContext";

export default function Decora() {
  const { baseUrl, baseUrlForFile, authToken } = useContext(AppContext);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [roomImageFile, setRoomImageFile] = useState(null);
  const [roomImage, setRoomImage] = useState(null);
  const [resultImage, setResultImage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    visibleProducts();
    Categories();
  }, []);

  const Categories = () => {
    axios
      .get(`${baseUrl}/customer/category/getCategories`)
      .then((response) => {
        console.log(response.data);
        setCategories(response.data.categories);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const visibleProducts = () => {
    axios
      .get(`${baseUrl}/customer/products/viewProducts`)
      .then((response) => {
        console.log(response.data);
        setProducts(response.data.products);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // only products matching the selected category
  const filteredProducts = useMemo(() => {
    if (!activeCategory) return products;
    return products.filter(
      (product) => product.adcategory_id === activeCategory.id,
    );
  }, [products, activeCategory]);

  const selectedProduct = useMemo(
    () => products.find((p) => p.id === selectedId) || null,
    [products, selectedId],
  );

  // close the category dropdown on an outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsCategoryOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectProduct = (id) => {
    setSelectedId((prev) => (prev === id ? null : id));
    setResultImage(null);
  };

  const handleRoomUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setRoomImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setRoomImage(ev.target.result);
      setResultImage(null);
    };
    reader.readAsDataURL(file);
  };

  // console.log(authToken);

  const handleVisualize = () => {
    if (!roomImage || !selectedId || isGenerating) return;
    setIsGenerating(true);
    setResultImage(null);

    console.log("Api called.");
    const formData = new FormData();
    formData.append("roomImage", roomImageFile);
    formData.append("product_id", selectedId);

    axios
      .post(`${baseUrl}/customer/decora/generateImage`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        console.log("Api response.");
        console.log(response);
        setResultImage(`${baseUrlForFile}/${response.data.data}`);
        setIsGenerating(false);
      })
      .catch((error) => {
        console.log(error);
        setIsGenerating(false);
      });
  };

  const canVisualize =
    Boolean(roomImage) && Boolean(selectedId) && !isGenerating;

  return (
    <div className="decora-page">
      <header className="decora-topbar">
        <Link to="/" className="decora-home-link">
          <FiHome aria-hidden="true" />
          Back home
        </Link>
        <div className="decora-brand">
          <span className="decora-brand-eyebrow">AD Furniture presents</span>
          <h1 className="decora-brand-name">Decora</h1>
        </div>
        <span className="decora-brand-tag">
          See it in your room before it's in your room.
        </span>
      </header>

      <main className="decora-body">
        {/* ---------- STYLE PANEL ---------- */}
        <section className="decora-panel" aria-label="Choose furniture">
          <div className="decora-panel-head">
            <h2>Pick your pieces</h2>
            <p>Choose a category and tap what catches your eye.</p>
          </div>

          <div className="decora-dropdown" ref={dropdownRef}>
            <button
              type="button"
              className="decora-dropdown-trigger"
              onClick={() => setIsCategoryOpen((open) => !open)}
              aria-haspopup="listbox"
              aria-expanded={isCategoryOpen}
            >
              <span>
                {activeCategory ? activeCategory.name : "All Categories"}
              </span>
              <FiChevronDown
                className={isCategoryOpen ? "is-open" : ""}
                aria-hidden="true"
              />
            </button>
            {isCategoryOpen && (
              <ul className="decora-dropdown-list" role="listbox">
                <li role="option" aria-selected={!activeCategory}>
                  <button
                    type="button"
                    className={!activeCategory ? "is-active" : ""}
                    onClick={() => {
                      setActiveCategory(null);
                      setIsCategoryOpen(false);
                    }}
                  >
                    All Categories
                  </button>
                </li>
                {categories?.map((cat) => (
                  <li
                    key={cat.id}
                    role="option"
                    aria-selected={activeCategory?.id === cat.id}
                  >
                    <button
                      type="button"
                      className={
                        activeCategory?.id === cat.id ? "is-active" : ""
                      }
                      onClick={() => {
                        setActiveCategory(cat);
                        setIsCategoryOpen(false);
                      }}
                    >
                      {cat.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="decora-product-grid">
            {filteredProducts?.map((product) => {
              const isSelected = selectedId === product.id;
              return (
                <button
                  key={product.id}
                  className={`decora-product-card ${isSelected ? "is-selected" : ""}`}
                  onClick={() => selectProduct(product.id)}
                  aria-pressed={isSelected}
                >
                  <span className="decora-product-thumb">
                    <img
                      src={`${baseUrlForFile}/${product?.productimages[0].image_path}`}
                      alt={product.name}
                    />
                    <span className="decora-select-mark" aria-hidden="true">
                      {isSelected ? <FiCheck /> : "+"}
                    </span>
                  </span>
                  <span className="decora-product-name">{product.name}</span>
                  <span className="decora-product-price">
                    Rs {product.price}
                  </span>
                </button>
              );
            })}
          </div>

          {/* ---- action zone: upload + visualize ---- */}
          <div className="decora-action-zone">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleRoomUpload}
              hidden
            />
            <button
              className="decora-btn decora-btn-outline"
              onClick={() => fileInputRef.current?.click()}
            >
              <FiUpload aria-hidden="true" /> Upload your room image
            </button>
            <button
              className="decora-btn decora-btn-solid"
              onClick={handleVisualize}
              disabled={!canVisualize}
            >
              <PiSparkleFill aria-hidden="true" />
              {isGenerating ? "Styling your room…" : "Visualize in my room"}
            </button>
            {!roomImage && (
              <p className="decora-hint">
                Upload a room photo to unlock this step.
              </p>
            )}
            {roomImage && !selectedId && (
              <p className="decora-hint">
                Select a piece above to visualize it.
              </p>
            )}
            {roomImage && selectedId && (
              <p className="decora-hint">
                Pick a different piece any time to try that one instead.
              </p>
            )}
          </div>
        </section>

        {/* ---------- ROOM CANVAS ---------- */}
        <section className="decora-canvas" aria-label="Your room preview">
          {!roomImage && (
            <div className="decora-canvas-empty">
              <span className="decora-canvas-empty-glyph" aria-hidden="true">
                <FiUpload />
              </span>
              <p>Your room hasn't arrived yet</p>
              <span>Upload a photo from the panel on the left to begin.</span>
            </div>
          )}

          {roomImage && (
            <div className="decora-canvas-frame">
              <button
                type="button"
                className="decora-fullscreen-trigger"
                onClick={() => setIsFullscreen(true)}
                aria-label="View room image fullscreen"
              >
                <img
                  src={resultImage || roomImage}
                  alt="Your room"
                  className={`decora-room-photo ${resultImage ? "is-styled" : ""}`}
                />
                <span className="decora-expand-mark" aria-hidden="true">
                  <FiMaximize2 />
                </span>
              </button>

              {isGenerating && (
                <div className="decora-loading-veil">
                  <span className="decora-loading-ring" aria-hidden="true" />
                  <p>Blending your picks into the room…</p>
                </div>
              )}

              {resultImage && !isGenerating && (
                <span className="decora-styled-badge">Styled by Decora</span>
              )}

              {selectedProduct && (
                <div className="decora-swatch-rail" aria-label="Selected piece">
                  <div className="decora-swatch">
                    <img
                      src={`${baseUrlForFile}/${selectedProduct.productimages[0].image_path}`}
                      alt={selectedProduct.name}
                    />
                    <button
                      className="decora-swatch-remove"
                      onClick={() => selectProduct(selectedProduct.id)}
                      aria-label={`Remove ${selectedProduct.name}`}
                    >
                      <FiX />
                    </button>
                  </div>
                  <span className="decora-swatch-label">
                    {selectedProduct.name}
                  </span>
                </div>
              )}
            </div>
          )}
        </section>
      </main>

      {/* ---------- FULLSCREEN VIEWER ---------- */}
      {isFullscreen && roomImage && (
        <div className="decora-lightbox" onClick={() => setIsFullscreen(false)}>
          <button
            className="decora-lightbox-close"
            onClick={() => setIsFullscreen(false)}
            aria-label="Close fullscreen view"
          >
            <FiX />
          </button>
          <img src={resultImage || roomImage} alt="Your room, fullscreen" />
        </div>
      )}
    </div>
  );
}

/**
 * Drop this anywhere on the customer Home page (e.g. after the hero
 * section) to give people an entry point into the Decora feature.
 */
export function DecoraHomeBanner() {
  return (
    <section className="decora-home-banner">
      <div>
        <span className="decora-brand-eyebrow">New · AI powered</span>
        <h2 className="decora-brand-name">
          Not sure how it'll look? Try it in your room.
        </h2>
        <p>
          Upload a photo of your space and preview our pieces in it before you
          buy.
        </p>
      </div>
      <Link to="/decora" className="decora-btn decora-btn-solid">
        <PiSparkleFill aria-hidden="true" /> Try Decora
      </Link>
    </section>
  );
}
